import { DesignerParams, DraftState, SharingDraft } from '@/types';
import { isResponseError } from '@/utils/http/methods/responseError';
import { getParams, isAdminMode } from '@/utils/params';
import { processErrorForCustomData, trackError } from '@/utils/raygun';
import { safeString } from '@/utils/safeString';
import { getProductType } from '@/model/draft/getProductType';
import { mapSharingDraftToDraftWithRedux } from '@/model/draftMapper/mapSharingDraftToDraftWithRedux';
import { isMementoAvailable } from '@/redux/draft/memento/isMementoAvailable';
import { StoreDispatch, StoreState } from '@/redux/types';
import {
  bootstrapWithBasketItem,
  bootstrapWithDraft,
  bootstrapWithEmptyProduct,
  bootstrapWithMemento,
  bootstrapWithProduct,
  bootstrapWithProductType,
  bootstrapWithSellable,
  bootstrapWithTemplate,
} from './bootstrap';

type BootstrapType =
  | 'basketItem'
  | 'product'
  | 'sellable'
  | 'draft'
  | 'template'
  | 'productType'
  | 'memento'
  | 'empty';

function trackBootstrapError(error: unknown, type: string) {
  if (isResponseError(error)) {
    // ajax request failed -> design/productType/product not available
    return;
  }

  trackError(`Failed to bootstrap with params [${type}]`, {
    error: processErrorForCustomData(error),
  });
}

export async function getInitialDraft(
  params: DesignerParams,
  dispatch: StoreDispatch,
  getState: () => StoreState
) {
  const {
    productTypeId,
    productId,
    designId,
    basketId,
    basketItemId: basketItemIdParam,
    draftId,
    sellableId,
    contextId,
    platform,
    templateId,
  } = params;

  const mementoAvailable = isMementoAvailable(
    contextId || getParams().contextId,
    platform || getParams().platform
  );

  let bootstrapType: BootstrapType | undefined;
  let sharingDraft: SharingDraft | undefined;
  let selectedSizes: DraftState['selectedSizes'] = {};
  let basketItemId: string | undefined;

  try {
    if (basketId && basketItemIdParam) {
      bootstrapType = 'basketItem';
      [sharingDraft, selectedSizes, basketItemId] = await bootstrapWithBasketItem(
        safeString(basketId),
        safeString(basketItemIdParam),
        safeString(productId),
        params,
        dispatch,
        getState
      );
    } else if (productId) {
      bootstrapType = 'product';
      sharingDraft = await bootstrapWithProduct(safeString(productId), params, getState);
    } else if (sellableId) {
      bootstrapType = 'sellable';
      sharingDraft = await bootstrapWithSellable(safeString(sellableId), params, getState);
    } else if (draftId) {
      bootstrapType = 'draft';
      sharingDraft = await bootstrapWithDraft(safeString(draftId));
    } else if (templateId) {
      bootstrapType = 'template';
      sharingDraft = await bootstrapWithTemplate(safeString(templateId));
    } else if (productTypeId || designId) {
      bootstrapType = 'productType';
      sharingDraft = await bootstrapWithProductType(params, dispatch, getState);
    } else if (mementoAvailable) {
      bootstrapType = 'memento';
      sharingDraft = bootstrapWithMemento(params);
    }
  } catch (error) {
    if (bootstrapType) {
      trackBootstrapError(error, bootstrapType);
    }
  }

  if (!sharingDraft) {
    bootstrapType = 'empty';
    sharingDraft = await bootstrapWithEmptyProduct(params, dispatch);
  }

  if (params.appearanceId) {
    sharingDraft.appearanceId = params.appearanceId;
  }

  let draft;
  try {
    draft = await mapSharingDraftToDraftWithRedux(sharingDraft, getState(), dispatch);
  } catch (error) {
    if (isAdminMode()) {
      console.error(error);
    }
    trackBootstrapError(error, bootstrapType!);
    if (bootstrapType !== 'empty') {
      sharingDraft = await bootstrapWithEmptyProduct(params, dispatch);
      draft = await mapSharingDraftToDraftWithRedux(sharingDraft, getState(), dispatch);
    }

    if (!draft) {
      throw new Error('Bootstrap With Empty Product failed');
    }
  }

  const sizeId = safeString(params.sizeId);
  if (sizeId && getProductType(draft).sizes.some((size) => size.id === sizeId)) {
    selectedSizes = { [safeString(params.sizeId)]: 1 };
  }

  const draftState: DraftState = {
    ...draft,
    selectedSizes,
    basketItemId,
    selectedConfigurationId: -1,
    validation: {
      valid: true,
      errors: {},
    },
    warnings: {},
  };

  return [draftState, bootstrapType] as const;
}
